import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import AffiliateLayout from '../../components/AffiliateLayout/AffiliateLayout';
import api from '../../services/api';
import {
  PageTitle, PageSub, Head, StatGrid, Tile, TileNum, TileLabel, Card, TableWrap,
  Table, Pill, Spinner, Empty, Avatar, NameCell, statusPill, money, colors,
  methodLabel, isRestored, payoutStatusText,
} from '../../components/AffiliateUI/AffiliateUI';

// Full affiliate roster with sales rolled up, ported from the standalone
// affiliates.zona.miami portal so the whole program is manageable from one
// admin login. Hits the same /affiliate-admin/* routes the standalone portal
// used — those already allow a full ZONA admin through (ensureAffiliateAdmin).

const initials = (name) =>
  (name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const Affiliates = () => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('available_cents');
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api.get('/affiliate-admin/affiliates')
      .then(({ data }) => setRows(data.affiliates || []))
      .catch(() => { toast.error('Failed to load affiliates'); setRows([]); });
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const term = q.trim().toLowerCase();
    let list = rows;
    if (term) {
      list = rows.filter((r) =>
        [r.name, r.email, r.phone, r.ig, r.promo_code].some((v) => v && v.toLowerCase().includes(term)));
    }
    return [...list].sort((a, b) => {
      if (sort === 'created_at') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      return (b[sort] || 0) - (a[sort] || 0);
    });
  }, [rows, q, sort]);

  const totals = useMemo(() => {
    const t = { count: 0, approved: 0, conversions: 0, available: 0 };
    (rows || []).forEach((r) => {
      t.count++;
      if (r.status === 'approved') t.approved++;
      t.conversions += r.conversions;
      t.available += r.available_cents;
    });
    return t;
  }, [rows]);

  return (
    <AffiliateLayout>
      {!rows ? <Spinner /> : (
            <>
              <Head>
                <div>
                  <PageTitle>Affiliates</PageTitle>
                  <PageSub>Everyone in the program, with their sales rolled up.</PageSub>
                </div>
              </Head>

              <StatGrid>
                <Tile><TileNum>{totals.count}</TileNum><TileLabel>Affiliates</TileLabel></Tile>
                <Tile><TileNum>{totals.approved}</TileNum><TileLabel>Approved</TileLabel></Tile>
                <Tile><TileNum>{totals.conversions}</TileNum><TileLabel>Total sales</TileLabel></Tile>
                <Tile><TileNum>{money(totals.available)}</TileNum><TileLabel>Owed now</TileLabel></Tile>
              </StatGrid>

              <Controls>
                <Search>
                  <Icon icon="solar:magnifer-outline" width={16} />
                  <input placeholder="Search name, email, IG, code…" value={q} onChange={(e) => setQ(e.target.value)} />
                </Search>
                <SortSelect value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="available_cents">Sort: Owed now</option>
                  <option value="conversions">Sort: Sales</option>
                  <option value="clicks">Sort: Clicks</option>
                  <option value="created_at">Sort: Newest</option>
                </SortSelect>
              </Controls>

              <Card>
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <th>Affiliate</th><th>Status</th><th className="num">Clicks</th>
                        <th className="num">Sales</th><th className="num">GMV</th>
                        <th className="num">Owed</th><th className="num">Paid</th><th>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((r) => (
                        <tr key={r._id} onClick={() => setOpenId(r._id)} style={{ cursor: 'pointer' }}>
                          <td>
                            <NameCell>
                              <Avatar src={r.photo}>{!r.photo && initials(r.name)}</Avatar>
                              <div>
                                <span className="n">{r.name || 'Unnamed'}</span>
                                {r.promo_code && (<><br /><span className="code">{r.promo_code}</span></>)}
                              </div>
                            </NameCell>
                          </td>
                          <td><Pill tone={statusPill(r.status)}>{r.status}</Pill></td>
                          <td className="num">{r.clicks}</td>
                          <td className="num strong">{r.conversions}</td>
                          <td className="num">{money(r.gmv_cents)}</td>
                          <td className="num strong">{money(r.available_cents)}</td>
                          <td className="num">{money(r.paid_out_cents)}</td>
                          <td>{fmtDate(r.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
                {filtered.length === 0 && <Empty>No affiliates match "{q}".</Empty>}
              </Card>

              {openId && <Drawer id={openId} onClose={() => setOpenId(null)} />}
            </>
      )}
    </AffiliateLayout>
  );
};

export default Affiliates;

const Drawer = ({ id, onClose }) => {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get(`/affiliate-admin/affiliates/${id}`)
      .then(({ data }) => setDetail(data))
      .catch(() => { toast.error('Failed to load affiliate'); onClose(); });
  }, [id, onClose]);

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <PanelBar>
          <strong>Affiliate detail</strong>
          <CloseBtn onClick={onClose}><Icon icon="solar:close-circle-outline" width={22} /></CloseBtn>
        </PanelBar>
        {!detail ? <Spinner /> : (
          <PanelBody>
            <Who>
              <Avatar src={detail.affiliate.photo} style={{ width: 44, height: 44 }}>
                {!detail.affiliate.photo && initials(detail.affiliate.name)}
              </Avatar>
              <div>
                <h3>{detail.affiliate.name || 'Unnamed'}</h3>
                <Pill tone={statusPill(detail.affiliate.status)}>{detail.affiliate.status}</Pill>
              </div>
            </Who>

            <Contact>
              {detail.affiliate.promo_code && <span><b>Code</b> {detail.affiliate.promo_code}</span>}
              {detail.affiliate.email && <span><b>Email</b> {detail.affiliate.email}</span>}
              {detail.affiliate.phone && <span><b>Phone</b> {detail.affiliate.phone}</span>}
              {detail.affiliate.ig && <span><b>IG</b> @{detail.affiliate.ig.replace(/^@/, '')}</span>}
              <span><b>Joined</b> {fmtDate(detail.affiliate.created_at)}</span>
            </Contact>

            <MiniGrid>
              <Mini><b>{detail.totals.clicks}</b><span>Clicks</span></Mini>
              <Mini><b>{detail.totals.conversions}</b><span>Sales</span></Mini>
              <Mini><b>{money(detail.totals.gmv_cents)}</b><span>GMV</span></Mini>
              <Mini><b>{money(detail.totals.pending_cents)}</b><span>Pending</span></Mini>
              <Mini><b>{money(detail.totals.available_cents)}</b><span>Owed now</span></Mini>
              <Mini><b>{money(detail.totals.paid_out_cents)}</b><span>Paid out</span></Mini>
            </MiniGrid>

            <SectionLabel>Recent earnings</SectionLabel>
            <Card>
              <TableWrap>
                <Table>
                  <thead>
                    <tr><th>Type</th><th>Event</th><th>Venue</th><th className="num">Amount</th><th>Status</th><th>When</th></tr>
                  </thead>
                  <tbody>
                    {detail.earnings.map((e) => (
                      <tr key={e._id}>
                        <td>{e.booking_type.replace(/_/g, ' ')}</td>
                        <td>{e.event_name || '—'}</td>
                        <td>{e.venue_name || '—'}</td>
                        <td className="num strong">{money(e.amount_cents)}</td>
                        <td><Pill tone={statusPill(e.status)}>{e.status}</Pill></td>
                        <td>{fmtDate(e.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
              {detail.earnings.length === 0 && <Empty>No earnings yet.</Empty>}
            </Card>

            <SectionLabel>Payout history</SectionLabel>
            <Card>
              <TableWrap>
                <Table>
                  <thead>
                    <tr><th className="num">Amount</th><th>Method</th><th>Status</th><th>Requested</th><th>Paid</th></tr>
                  </thead>
                  <tbody>
                    {detail.payouts.map((p) => (
                      <tr key={p._id}>
                        <td className="num strong">{money(p.amount_cents)}</td>
                        <td>{methodLabel(p.method)}</td>
                        <td><Pill tone={isRestored(p) ? 'muted' : statusPill(p.status)}>{payoutStatusText(p)}</Pill></td>
                        <td>{fmtDate(p.requested_at)}</td>
                        <td>{fmtDate(p.paid_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
              {detail.payouts.length === 0 && <Empty>No payouts yet.</Empty>}
            </Card>
          </PanelBody>
        )}
      </Panel>
    </Overlay>
  );
};

const Controls = styled.div`display: flex; gap: 10px; margin-bottom: 14px; flex-wrap: wrap;`;
const Search = styled.label`
  flex: 1; min-width: 200px; display: flex; align-items: center; gap: 8px;
  height: 40px; padding: 0 14px; background: ${colors.bgInput};
  border: 1px solid ${colors.border}; border-radius: 10px; color: ${colors.textMuted};
  input { flex: 1; background: none; border: none; outline: none; color: #fff; font-size: 14px; }
  input::placeholder { color: ${colors.textFaint}; }
`;
const SortSelect = styled.select`
  height: 40px; padding: 0 12px; background: ${colors.bgInput};
  border: 1px solid ${colors.border}; border-radius: 10px; color: ${colors.textSecondary};
  font-size: 13px; outline: none; cursor: pointer;
`;

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 300;
  background: rgba(0,0,0,0.6); backdrop-filter: blur(4px);
  display: flex; justify-content: flex-end;
`;
const Panel = styled.div`
  width: 100%; max-width: 640px; height: 100%; overflow-y: auto;
  background: ${colors.bg}; border-left: 1px solid ${colors.border};
`;
const PanelBar = styled.div`
  position: sticky; top: 0; z-index: 2;
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; background: rgba(5,5,5,0.9); backdrop-filter: blur(20px);
  border-bottom: 1px solid ${colors.border};
  strong { color: #fff; font-size: 14px; }
`;
const CloseBtn = styled.button`background: none; border: none; color: ${colors.textMuted}; cursor: pointer; &:hover { color: #fff; }`;
const PanelBody = styled.div`padding: 20px;`;
const Who = styled.div`
  display: flex; align-items: center; gap: 14px; margin-bottom: 16px;
  h3 { color: #fff; font-size: 18px; font-weight: 700; margin: 0 0 6px; }
`;
const Contact = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px 18px; margin-bottom: 20px;
  font-size: 13px; color: ${colors.textSecondary};
  b { color: ${colors.textMuted}; font-weight: 600; margin-right: 5px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
`;
const MiniGrid = styled.div`display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 24px;`;
const Mini = styled.div`
  background: ${colors.bgCard}; border: 1px solid ${colors.border};
  border-radius: 12px; padding: 12px;
  b { display: block; color: #fff; font-size: 17px; font-weight: 800; }
  span { color: ${colors.textMuted}; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
`;
const SectionLabel = styled.div`
  color: ${colors.textMuted}; font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.8px; margin: 22px 0 10px;
`;
